import { useTranslator } from '@bluebeam/react-i18n';
import { ModalStaticProperties } from 'components/ModalPortal/ModalPortal';
import ExportConfirmationStep from 'components/modals/ExportDataModal/ExportConfirmationStep';
import IntegrationsBrowseFilesStep from 'components/modals/ExportDataModal/IntegrationsBrowseFilesStep';
import SelectAccountStep from 'components/modals/ExportDataModal/SelectAccountStep';
import SelectFilesStep from 'components/modals/ExportDataModal/SelectFilesStep';
import ModalStdHeader from 'components/modals/ModalStdHeader';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { FC, useContext, useEffect, useState } from 'react';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { StudioProjectFolder } from 'records/StudioProjectFolder';
import { WithAppStoreProps } from 'utils/withAppStore';
import type { FilesExport } from './SelectFilesStep';
import StudioBrowseFilesStep from './StudioBrowseFilesStep';

interface Props extends WithAppStoreProps {
  exportTo: 'studio' | 'integrations';
}

const SELECT_ACCOUNT = 'SELECT_ACCOUNT';
const SELECT_FILES = 'SELECT_FILES';
const INTEGRATION_FILES_BROWSER = 'INTEGRATION_FILES_BROWSER';
const EXPORT_CONFIRMATION = 'EXPORT_CONFIRMATION';
const CLOSE = 'CLOSE';
const ROOT = 'ROOT';

type Step = {
  previousStep: string;
  currentStep: string;
  nextStep: string;
};

const integrationSteps = new Map<number, Step>();
integrationSteps
  .set(0, {
    previousStep: ROOT,
    currentStep: SELECT_FILES,
    nextStep: SELECT_ACCOUNT,
  })
  .set(1, {
    previousStep: SELECT_FILES,
    currentStep: SELECT_ACCOUNT,
    nextStep: INTEGRATION_FILES_BROWSER,
  })
  .set(2, {
    previousStep: SELECT_ACCOUNT,
    currentStep: INTEGRATION_FILES_BROWSER,
    nextStep: EXPORT_CONFIRMATION,
  })
  .set(3, {
    previousStep: INTEGRATION_FILES_BROWSER,
    currentStep: EXPORT_CONFIRMATION,
    nextStep: CLOSE,
  });

const ExportDataModal: FC<Props> & ModalStaticProperties = ({ exportTo }) => {
  const { translate } = useTranslator(['requestDownloadModal', 'buttons']);
  const { actions, state } = useContext(AppStoreContext);
  const [currentStepId, setCurrentStepId] = useState(0);
  const [filesToExport, setFilesToExport] = useState<FilesExport>({
    drawings: true,
    punch: true,
    rfis: true,
    submittals: true,
    users: true,
  });

  useEffect(() => {
    actions.resetBreadcrumbs();
    actions.resetStudioView();
    actions.resetIntegrationsView();
    actions.resetIntegrationsSelectedItems();
    actions.setStudioSelectedFiles([]);
    actions.setStudioSelectedFolders([]);
    actions.setStudioExportFolder(new StudioProjectFolder());
    actions.clearStudioNavigation();
    getConnectedAccounts();
  }, []);

  const getConnectedAccounts = async (): Promise<void> => {
    await actions.getConnectedAccounts();
  };

  const getCurrentStep = (): Step | undefined => {
    return integrationSteps.get(currentStepId);
  };

  const handleOnConnectionClick = async (
    connectedAccountId: string
  ): Promise<void> => {
    await actions.setIntegrationConnectedAccountId(connectedAccountId);
  };

  const dispatch = () => {
    const currentStep = integrationSteps.get(currentStepId);
    switch (currentStep?.currentStep) {
      case SELECT_ACCOUNT:
        return (
          <SelectAccountStep
            accounts={state.connectedAccounts}
            onClick={handleOnConnectionClick}
          />
        );
      case INTEGRATION_FILES_BROWSER:
        if (exportTo === 'studio') {
          return <StudioBrowseFilesStep />;
        }
        return <IntegrationsBrowseFilesStep />;
      case EXPORT_CONFIRMATION:
        return <ExportConfirmationStep />;
      default:
        return (
          <SelectFilesStep
            selectedFiles={filesToExport}
            type="export"
            onChange={(exportFiles: FilesExport) => {
              setFilesToExport(exportFiles);
            }}
          />
        );
    }
  };

  const handleOnNextClick = (): void => {
    if (exportTo === 'studio' && currentStepId === 0) {
      setCurrentStepId((prevCurrentStepId) => prevCurrentStepId + 2);
    } else {
      setCurrentStepId((prevCurrentStepId) => prevCurrentStepId + 1);
    }
  };

  const handleOnExportClick = async (): Promise<void> => {
    if (exportTo === 'studio') {
      await actions.exportAllDataToStudio(filesToExport);
    } else {
      await actions.exportProjectDataToIntegrationsFolder(filesToExport);
    }

    handleOnNextClick();
  };

  const handleOnPreviousClick = (): void => {
    if (exportTo === 'studio' && currentStepId === 2) {
      setCurrentStepId((prevCurrentStepId) => prevCurrentStepId - 2);
    } else {
      setCurrentStepId((prevCurrentStepId) => prevCurrentStepId - 1);
    }
  };

  const isDisabled = (): boolean => {
    const currentStep = getCurrentStep();

    const fileTypeIsSelected = Object.values(filesToExport).some((val) => val);

    if (currentStep?.currentStep === SELECT_FILES) {
      return !fileTypeIsSelected;
    }
    if (currentStep?.currentStep === SELECT_ACCOUNT) {
      return !state.integrationsConnectedAccountId.length;
    }
    if (currentStep?.currentStep === INTEGRATION_FILES_BROWSER) {
      if (exportTo === 'studio') {
        return state.studioExportFolder === null;
      }
      const firstSelectedItem =
        state.integrationsSelectedItems.first() as IntegrationsSelectedItem;
      return !firstSelectedItem || firstSelectedItem.itemType !== 'folder';
    }
    return false;
  };

  const handleOnModalClose = async () => {
    await actions.clearIntegrationsNavigation();
    await actions.setStudioExportFolder(null);
    await actions.setStudioProjectId('');
    await actions.resetBreadcrumbs();
    await actions.clearAllModals();
    await actions.clearStudioNavigation();
  };

  return (
    <div id="export-project-data" className="modal__content oh column">
      <ModalStdHeader
        text={translate('requestDownloadModal:exportModal.header')}
        onClose={handleOnModalClose}
      />
      {dispatch()}
      <div
        className="row px5 py3 so-vw100 w100 bgd-white-2 justify-between modal__button-row modal__button-row--with-border oa"
        style={{ maxHeight: '84vh' }}
      >
        {getCurrentStep()?.nextStep !== CLOSE ? (
          <button
            id="integrations-modal-cancel"
            className="btn btn-link"
            onClick={async () => {
              await actions.clearIntegrationsNavigation();
              await actions.clearStudioNavigation();
              await actions.resetBreadcrumbs();
              await actions.clearAllModals();
            }}
          >
            {translate('buttons:cancel')}
          </button>
        ) : null}

        {getCurrentStep()?.nextStep !== CLOSE ? (
          <div className="row">
            {getCurrentStep()?.previousStep === ROOT ? null : (
              <button
                id="integrations-modal-reconnect"
                className="btn btn-link mr3"
                disabled={isDisabled()}
                onClick={handleOnPreviousClick}
              >
                {translate('buttons:previous')}
              </button>
            )}
            {getCurrentStep()?.currentStep === INTEGRATION_FILES_BROWSER ? (
              <button
                id="integrations-modal-submit"
                data-analytics={
                  exportTo === 'integrations'
                    ? 'export-to-integrations-submit-button'
                    : undefined
                }
                className="btn btn-primary"
                onClick={handleOnExportClick}
                disabled={isDisabled()}
              >
                {translate('buttons:export')}
              </button>
            ) : (
              <button
                id="integrations-modal-submit"
                className="btn btn-primary"
                disabled={isDisabled()}
                onClick={handleOnNextClick}
              >
                {translate('buttons:next')}
              </button>
            )}
          </div>
        ) : null}
        {getCurrentStep()?.nextStep === CLOSE ? (
          <div className="row justify-end w100">
            <button
              id="integrations-modal-cancel"
              className="btn btn-link"
              onClick={handleOnModalClose}
            >
              {translate('buttons:close')}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

ExportDataModal.modalClasses = {
  overlay: `modal__overlay`,
  content: `modal__window modal--sm`,
};

ExportDataModal.modalStyles = {
  content: {
    minHeight: '576px',
    width: '540px',
  },
};

export default ExportDataModal;
